// Redux
import { Action, Result, AsyncResult, ApiAction } from '../index';
import api from '../redux-api';

import { getComparisonBenchmarkData, getManyComparisonBenchmarkData } from './comparison-benchmarks/comparison-benchmarks-store';
import { setComparisonIsFocused, loadBenchmarkForEdit } from './common/actionCreators';

// Utils
import { COMPARISON_ITEM_TYPES } from '../../utils/constants';
import { deepClone } from '../../utils/objects';
import { calculateBenchmarkPortfolioStats } from '../../utils/comparison/benchmarks';
import { getCurrentStateComparison, shouldUseMonthlyRollingInReturns } from '../../utils/comparison';
import { generateTempId } from '../../utils/random';

// Types
import { EditBenchmarkState } from './types/edit-benchmark';
import { LOAD_BENCHMARK_FOR_EDIT } from './common/actions';
import ComparisonHolding from '@/types/comparison-holding';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import BenchmarkHolding from '@/types/benchmark-holding';
import BenchmarkPortfolio from '@/types/benchmark-portfolio';

const UPDATE_HOLDINGS: Action = {
  type: 'connect/edit-benchmark/holdings/update',
};

const SET_NAME: Action = {
  type: 'connect/edit-benchmark/name',
};

const ADD_HOLDING: Action = {
  type: 'connect/edit-benchmark/holdings/add',
};

const REMOVE_HOLDING: Action = {
  type: 'connect/edit-benchmark/holdings/remove',
};

const CANCEL_BENCHMARK_EDITING: Action = {
  type: 'connect/edit-benchmark/cancel-benchmark-editing',
};

const SAVE_BENCHMARK_PORTFOLIO: ApiAction = {
  type: 'connect/edit-benchmark/save',
  endpoint: 'benchmark-portfolios/',
};

const UPDATE_BENCHMARK_PORTFOLIO_BY_ID: ApiAction = {
  type: 'connect/edit-benchmark/update-by-id',
  endpoint: 'benchmark-portfolios/:id/',
};

// Initial State
const INITIAL_STATE: EditBenchmarkState = {
  editedBenchmark: undefined,
};

// Reducer
export default function reducer(state: EditBenchmarkState = INITIAL_STATE, action: Action): EditBenchmarkState {
  switch (action.type) {
    case UPDATE_HOLDINGS.type: {
      if (!state.editedBenchmark) {
        return state;
      }

      return {
        ...state,
        editedBenchmark: {
          ...state.editedBenchmark,
          holdings: action.holdings,
        },
      };
    }
    case SET_NAME.type: {
      if (!state.editedBenchmark) {
        return state;
      }

      return {
        ...state,
        editedBenchmark: {
          ...state.editedBenchmark,
          name: action.name,
        },
      };
    }
    case ADD_HOLDING.type: {
      if (!state.editedBenchmark) {
        return state;
      }

      return {
        ...state,
        editedBenchmark: {
          ...state.editedBenchmark,
          holdings: state.editedBenchmark.holdings.concat([action.holding]),
        },
      };
    }
    case REMOVE_HOLDING.type: {
      if (!state.editedBenchmark) {
        return state;
      }

      return {
        ...state,
        editedBenchmark: {
          ...state.editedBenchmark,
          holdings: state.editedBenchmark.holdings.filter(item => item.id !== action.holdingId),
        },
      };
    }
    case LOAD_BENCHMARK_FOR_EDIT.type: {
      return {
        ...state,
        editedBenchmark: action.benchmark,
      };
    }
    case CANCEL_BENCHMARK_EDITING.type: {
      return {
        ...state,
        editedBenchmark: undefined,
      };
    }
    default:
      return state;
  }
}

export const cancelBenchmarkEditing = (): Result<void> => dispatch => {
  dispatch({ ...CANCEL_BENCHMARK_EDITING });
  dispatch(setComparisonIsFocused(false));
};

export const addBenchmarkToPortfolioById =
  (id: string): AsyncResult<void> =>
  async (dispatch, getState) => {
    const { editedBenchmark } = getState().editBenchmarks;
    if (!editedBenchmark) {
      throw new Error('No current edited benchmark');
    }

    if (editedBenchmark.holdings.some(holding => holding.id === id)) {
      return Promise.reject({ errorKey: 'BENCHMARK_ALREADY_ADDED' });
    }

    return dispatch(getComparisonBenchmarkData(id))
      .then(({ benchmark }) => {
        const holding: ComparisonHolding = {
          name: benchmark.name,
          rolling: benchmark.performance.rolling,
          symbol: benchmark.ticker,
          weight: 0,
          id: benchmark._id,
          type: COMPARISON_ITEM_TYPES.BENCHMARK,
          riskFreeId: benchmark.riskFreeId,
          currency: benchmark.currency,
        };

        dispatch({ type: ADD_HOLDING.type, holding });
      })
      .catch(error => {
        dispatch({ type: `${UPDATE_HOLDINGS.type}_ERROR`, error });
      });
  };

export const addBenchmarksToPortfolio =
  (benchmarks: ComparisonBenchmark[]): AsyncResult<void[]> =>
  dispatch => {
    const promises = benchmarks.map(b => dispatch(addBenchmarkToPortfolioById(b._id)));
    return Promise.all(promises);
  };

export const removeBenchmarkHoldingById =
  (holdingId: string): Result<void> =>
  dispatch => {
    dispatch({ ...REMOVE_HOLDING, holdingId });
  };

export const changeHoldingWeight =
  (holdingId: string, weight: number): Result<void> =>
  (dispatch, getState) => {
    const { editedBenchmark } = getState().editBenchmarks;
    if (!editedBenchmark) {
      return;
    }

    const holdings = deepClone(editedBenchmark.holdings) as BenchmarkHolding[];

    const holding = holdings.find(h => h.id === holdingId);
    if (holding) {
      holding.weight = weight;
    }

    dispatch({ ...UPDATE_HOLDINGS, holdings });
  };

export const loadNewBenchmark = (): Result<void> => dispatch => {
  const benchmarkPortfolio: BenchmarkPortfolio = {
    _id: generateTempId(),
    name: '',
    holdings: [],
    dates: {},
  };

  dispatch(loadBenchmarkForEdit(benchmarkPortfolio));
};

export const saveBenchmark =
  (benchmarkPortfolio: BenchmarkPortfolio): AsyncResult<BenchmarkPortfolio> =>
  async (dispatch, getState) => {
    if (benchmarkPortfolio.holdings.some(h => h.weight === 0)) {
      return Promise.reject({ errorKey: 'BENCHMARK_EMPTY_HOLDING' });
    }

    const comparison = getCurrentStateComparison(getState().comparison);

    const shortHistory = comparison && comparison.shortHistory;
    const benchmarkData = await dispatch(getManyComparisonBenchmarkData(benchmarkPortfolio.holdings.map(h => h.id)));
    const forcedMonthlyRolling: boolean = comparison != null ? shouldUseMonthlyRollingInReturns(comparison, getState().auth.user) : false;
    const newBenchmarkPortfolio = await calculateBenchmarkPortfolioStats(
      dispatch,
      benchmarkPortfolio,
      benchmarkData,
      forcedMonthlyRolling,
      shortHistory,
      comparison?.commonInception,
    );

    const data: any = { ...newBenchmarkPortfolio };

    let upsertPromise;
    if (data._id && (!data.dates.savedAt || data._id.startsWith('new-'))) {
      delete data._id;
      upsertPromise = api.post(SAVE_BENCHMARK_PORTFOLIO, null, data);
    } else {
      upsertPromise = api.put(UPDATE_BENCHMARK_PORTFOLIO_BY_ID, { id: data._id }, data);
    }

    return upsertPromise.then((res: { data: any }) => {
      dispatch(setComparisonIsFocused(true));
      return res.data;
    });
  };

export const allocateRemaining = (): Result<void> => (dispatch, getState) => {
  const { editedBenchmark } = getState().editBenchmarks;

  if (!editedBenchmark) {
    return;
  }

  const holdings = deepClone(editedBenchmark.holdings) as BenchmarkHolding[];

  const emptyHoldings = holdings.filter(h => h.weight === 0);
  const unallocatedHoldings = emptyHoldings.length ? emptyHoldings : holdings;

  const unallocatedWeight = 100 - holdings.reduce((total, holding) => total + holding.weight, 0);

  const remainder = unallocatedWeight / unallocatedHoldings.length;
  for (const unallocated of unallocatedHoldings) {
    const holding = holdings.find(h => h.id === unallocated.id);

    if (holding) {
      holding.weight += remainder;
    }
  }

  dispatch({ ...UPDATE_HOLDINGS, holdings });
};

export const allocateEvenly = (): Result<void> => (dispatch, getState) => {
  const { editedBenchmark } = getState().editBenchmarks;

  if (!editedBenchmark) {
    return;
  }

  const holdings = deepClone(editedBenchmark.holdings) as BenchmarkHolding[];

  const remainder = 100 / holdings.length;

  for (const holding of holdings) {
    holding.weight = remainder;
  }

  dispatch({ ...UPDATE_HOLDINGS, holdings });
};

export const setBenchmarkPortfolioName =
  (name: string): Result<void> =>
  dispatch => {
    dispatch({ type: SET_NAME.type, name });
  };
